import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'
import { ApplicationNote } from '../services/jobApplicationService'
import { JobApplication } from './ApplicationWizard'
import { NoteForm } from './NoteForm'
import { NoteItem } from './NoteItem'
import { LoadingSpinner } from './LoadingSpinner'
import './NotesPanel.css'

interface NotesPanelProps {
  application: JobApplication
  onClose: () => void
  onSaveNote: (note: ApplicationNote) => Promise<void>
  onUpdateNote: (noteId: string, note: ApplicationNote) => Promise<void>
  onDeleteNote: (noteId: string) => Promise<void>
  onLoadNotes: (applicationId: string) => Promise<ApplicationNote[]>
}

export const NotesPanel: React.FC<NotesPanelProps> = ({
  application,
  onClose,
  onSaveNote,
  onUpdateNote,
  onDeleteNote,
  onLoadNotes
}) => {
  const [isMounted, setIsMounted] = useState(false)
  const [notes, setNotes] = useState<ApplicationNote[]>([])
  const [isLoading, setIsLoading] = useState(true)
  
  useEffect(() => {
    setIsMounted(true)
  }, [])
  const [editingNote, setEditingNote] = useState<ApplicationNote | undefined>(undefined)

  // Load notes when panel opens
  useEffect(() => {
    if (application.id) {
      loadNotes()
    }
  }, [application.id])

  const loadNotes = async () => {
    if (!application.id) return
    
    try {
      setIsLoading(true)
      const loadedNotes = await onLoadNotes(application.id)
      setNotes(loadedNotes || [])
    } catch (error) {
      toast.error('Failed to load notes')
      setNotes([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveNote = async (note: ApplicationNote) => {
    try {
      if (editingNote) {
        // Update existing note
        await onUpdateNote(note.id, note)
        setEditingNote(undefined)
      } else {
        // Add new note
        await onSaveNote(note)
      }
      // Reload notes to get the updated list from backend
      await loadNotes()
    } catch (error) {
      toast.error(`Failed to save note: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleDeleteNote = async (noteId: string) => {
    try {
      await onDeleteNote(noteId)
      await loadNotes()
    } catch (error) {
      toast.error('Failed to delete note')
    }
  }

  const handleEditNote = (note: ApplicationNote) => {
    setEditingNote(note)
  }

  const handleCancelEdit = () => {
    setEditingNote(undefined)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Only render portal after component is mounted (client-side only)
  // This prevents React error #310 (hydration mismatch)
  if (!isMounted || typeof document === 'undefined' || !document.body) {
    return null
  }

  return createPortal(
    <div className="notes-panel-backdrop" onClick={handleBackdropClick}>
      <div 
        className="notes-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="notes-panel-header">
          <div className="notes-panel-title">
            <h2>Notes</h2>
            <p className="notes-panel-subtitle">
              {application.company || 'Unknown Company'} - {application.position || 'No Position'}
            </p>
          </div>
          <button 
            className="notes-panel-close"
            onClick={onClose}
            title="Close notes panel"
          >
            ×
          </button>
        </div>

        {/* Form Section - Fixed */}
        <div className="notes-panel-form-section">
          {isLoading ? (
            <div className="notes-panel-loading">
              <LoadingSpinner size="medium" />
              <p>Loading notes...</p>
            </div>
          ) : (
            <NoteForm
              note={editingNote}
              onSave={handleSaveNote}
              onCancel={editingNote ? handleCancelEdit : undefined}
            />
          )}
        </div>

        {/* Notes List - Scrollable */}
        <div className="notes-panel-scrollable">
          {isLoading ? (
            <div className="notes-panel-loading">
              <LoadingSpinner size="medium" />
              <p>Loading notes...</p>
            </div>
          ) : (
            <div className="notes-panel-list">
              {notes.length === 0 ? (
                <div className="notes-panel-empty">
                  <p>No notes yet. Add your first note above!</p>
                </div>
              ) : (
                notes.map((note) => (
                  <NoteItem
                    key={note.id}
                    note={note}
                    onEdit={handleEditNote}
                    onDelete={handleDeleteNote}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

